import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from '../common/Loader'
import Alert from '../common/Alert'
import vehicleContractService from '../../services/vehicleContractService'
import vehicleDailyTripService from '../../services/vehicleDailyTripService'
import vehicleContractPaymentService from '../../services/vehicleContractPaymentService'
import AddVehicleDailyTripModal from './vehicle-daily-trip/AddVehicleDailyTripModal'
import EditVehicleDailyTripModal from './vehicle-daily-trip/EditVehicleDailyTripModal'
import ViewVehicleDailyTripModal from './vehicle-daily-trip/ViewVehicleDailyTripModal'
import AddVehicleContractPaymentModal from './vehicle-contract-payment/AddVehicleContractPaymentModal'
import EditVehicleContractPaymentModal from './vehicle-contract-payment/EditVehicleContractPaymentModal'
import ViewVehicleContractPaymentModal from './vehicle-contract-payment/ViewVehicleContractPaymentModal'
import { ArrowLeft, Edit3, Plus, Download, ScrollText } from 'lucide-react'
import Button from '../common/Button.js'
import RowActionsMenu from '../common/RowActionsMenu.js'
import { useTranslation } from 'react-i18next';
import { downloadBlob } from '../../utils/downloadBlob.js';


const ViewVehicleContract = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { contractId } = useParams()
  const [loading, setLoading] = useState(true)
  const [contract, setContract] = useState(null)
  const [dailyTrips, setDailyTrips] = useState([])
  const [payments, setPayments] = useState([])
  const [tripsLoading, setTripsLoading] = useState(false)
  const [paymentsLoading, setPaymentsLoading] = useState(false)

  const [showAddTrip, setShowAddTrip] = useState(false)
  const [editTrip, setEditTrip] = useState(null)
  const [viewTrip, setViewTrip] = useState(null)
  const [showAddPayment, setShowAddPayment] = useState(false)
  const [editPayment, setEditPayment] = useState(null)
  const [viewPayment, setViewPayment] = useState(null)
  const [openTripActionId, setOpenTripActionId] = useState(null)
  const [openPaymentActionId, setOpenPaymentActionId] = useState(null)
  const tripActionRef = useRef(null)
  const paymentActionRef = useRef(null)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    fetchContract()
    fetchTrips()
    fetchPayments()
  }, [contractId])

  useEffect(() => {
    if (!openTripActionId && !openPaymentActionId) return undefined
    const handleClickOutside = (event) => {
      if (
        tripActionRef.current?.contains(event.target) ||
        paymentActionRef.current?.contains(event.target)
      ) {
        return
      }
      setOpenTripActionId(null)
      setOpenPaymentActionId(null)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openTripActionId, openPaymentActionId])

  const fetchContract = async () => {
    try {
      setLoading(true)
      const response = await vehicleContractService.getOne(contractId)
      setContract(response.data.data)
    } catch (err) {
      Alert.error('Failed to load contract')
      console.error(err)
      navigate('/vehicle-contracts')
    } finally {
      setLoading(false)
    }
  }

  const fetchTrips = async () => {
    try {
      setTripsLoading(true)
      const response = await vehicleDailyTripService.getByContract(contractId)
      setDailyTrips(response.data.data || [])
    } catch (err) {
      console.error('Failed to load daily trips:', err)
    } finally {
      setTripsLoading(false)
    }
  }

  const fetchPayments = async () => {
    try {
      setPaymentsLoading(true)
      const response = await vehicleContractPaymentService.getByContract(contractId)
      setPayments(response.data.data || [])
    } catch (err) {
      console.error('Failed to load payments:', err)
    } finally {
      setPaymentsLoading(false)
    }
  }

  const handleDeleteTrip = async (tripId) => {
    const confirmed = await Alert.confirm('Are you sure you want to delete this trip?', 'Delete Trip')
    if (!confirmed) return

    try {
      await vehicleDailyTripService.delete(tripId)
      Alert.success('Trip deleted successfully!')
      fetchTrips()
      fetchContract()
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete trip')
    }
  }

  const handleDeletePayment = async (paymentId) => {
    const confirmed = await Alert.confirm('Are you sure you want to delete this payment?', 'Delete Payment')
    if (!confirmed) return

    try {
      await vehicleContractPaymentService.delete(paymentId)
      Alert.success('Payment deleted successfully!')
      fetchPayments()
      fetchContract()
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete payment')
    }
  }

  const handleExportInvoice = async () => {
    try {
      setExporting(true)
      const response = await vehicleContractService.downloadInvoice(contractId, true)
      await downloadBlob(
        response.data,
        `contract_${contract.contract_id}.pdf`,
        'application/pdf',
      )

      Alert.success('Invoice downloaded successfully!')
    } catch (err) {
      Alert.error('Failed to download invoice')
      console.error(err)
    } finally {
      setExporting(false)
    }
  }

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const isExpired = (toDate) => {
    if (!toDate) return false
    const today = new Date()
    const expiryDate = new Date(toDate)
    return expiryDate < today
  }

  const isExpirySoon = (toDate) => {
    if (!toDate) return false
    const today = new Date()
    const expiryDate = new Date(toDate)
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24))
    return daysUntilExpiry >= 0 && daysUntilExpiry <= 5
  }

  const getExpiryStatus = (toDate) => {
    if (isExpired(toDate)) {
      return { label: 'Expired', color: 'bg-red-100 text-red-700 border-red-200' }
    }
    if (isExpirySoon(toDate)) {
      return { label: 'Expiring Soon', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' }
    }
    return { label: 'Active', color: 'bg-green-100 text-green-700 border-green-200' }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading contract..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!contract) {
    return null
  }

  const expiryStatus = getExpiryStatus(contract.to_date)

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-center mb-4 sm:mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex flex-wrap items-center gap-2 sm:gap-3">
            <ScrollText className="w-6 h-6 text-buttonhoverTxt shrink-0" />
            <span className="font-mono text-orange-600">{contract.contract_id}</span>
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1 break-words">
            {contract.contract_party_name} · {contract.vehicle_number}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 w-full lg:w-auto">
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/vehicle-contracts')}>
            Back to List
          </Button>
          <Button variant="primary" icon={Edit3} onClick={() => navigate(`/vehicle-contracts/edit/${contract.id}`)}>
            Edit
          </Button>
          <Button variant="secondary" icon={Download} loading={exporting} disabled={exporting} onClick={handleExportInvoice}>
            Download Invoice
          </Button>
        </div>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-orange-500">
          <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Duration</p>
          <p className="text-sm sm:text-base font-bold text-gray-900">
            {formatDate(contract.from_date)} – {formatDate(contract.to_date)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-blue-500">
          <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Status</p>
          <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold border ${expiryStatus.color}`}>
            {expiryStatus.label}
          </span>
        </div>
        <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-green-500">
          <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Total Cost</p>
          <p className="text-base sm:text-lg font-bold text-gray-900">{formatCurrency(contract.total_contracted_cost)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-purple-500">
          <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Paid / Balance</p>
          <p className="text-base sm:text-lg font-bold text-green-600">{formatCurrency(contract.paid_amount)}</p>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{formatCurrency(contract.balance_contracted_cost)} balance</p>
        </div>
      </div>

      {/* Daily Trips */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-bold text-gray-700">Daily Trips</h2>
          <Button variant="success" icon={Plus} onClick={() => setShowAddTrip(true)} className="w-full sm:w-auto">
            Add Trip
          </Button>
        </div>

        {tripsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader fullScreen={false} size="default" message="Loading trips..." />
          </div>
        ) : dailyTrips.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No daily trips recorded yet</div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[700px] border-collapse">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Running KM</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Daily Diff</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Extra Charge</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Driver</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {dailyTrips.map((trip, index) => (
                    <tr key={trip.id} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">{formatDate(trip.trip_date)}</td>
                      <td className="px-5 py-3 text-right text-sm text-gray-900">{trip.running_km.toFixed(2)} KM</td>
                      <td className={`px-5 py-3 text-right text-sm font-semibold ${trip.daily_diff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {trip.daily_diff.toFixed(2)}
                      </td>
                      <td className="px-5 py-3 text-right text-sm font-semibold text-gray-900">{formatCurrency(trip.extra_km_charge)}</td>
                      <td className="px-5 py-3 text-sm text-gray-900">{trip.driver || 'N/A'}</td>
                      <td className="px-5 py-3 text-center">
                        <RowActionsMenu
                          isOpen={openTripActionId === trip.id}
                          onToggle={() => setOpenTripActionId(openTripActionId === trip.id ? null : trip.id)}
                          onClose={() => setOpenTripActionId(null)}
                          menuRef={tripActionRef}
                          onView={() => setViewTrip(trip.id)}
                          onEdit={() => setEditTrip(trip.id)}
                          onDelete={() => handleDeleteTrip(trip.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3 p-4">
              {dailyTrips.map((trip) => (
                <div key={`trip-card-${trip.id}`} className="rounded-xl border border-gray-100 p-4 border-l-4 border-l-orange-500">
                  <div className="flex justify-between items-start mb-3 gap-2">
                    <p className="font-semibold text-gray-800 text-sm">{formatDate(trip.trip_date)}</p>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                        trip.daily_diff > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {trip.daily_diff > 0 ? 'Over Limit' : 'Within Limit'}
                      </span>
                      <RowActionsMenu
                        isOpen={openTripActionId === trip.id}
                        onToggle={() => setOpenTripActionId(openTripActionId === trip.id ? null : trip.id)}
                        onClose={() => setOpenTripActionId(null)}
                        menuRef={tripActionRef}
                        onView={() => setViewTrip(trip.id)}
                        onEdit={() => setEditTrip(trip.id)}
                        onDelete={() => handleDeleteTrip(trip.id)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Running KM</p>
                      <p className="font-semibold text-gray-800">{trip.running_km.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Daily Diff</p>
                      <p className={`font-semibold ${trip.daily_diff > 0 ? 'text-red-600' : 'text-green-600'}`}>{trip.daily_diff.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Extra Charge</p>
                      <p className="font-semibold text-gray-800">{formatCurrency(trip.extra_km_charge)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Driver</p>
                      <p className="font-semibold text-gray-800">{trip.driver || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Payments */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-bold text-gray-700">Payments</h2>
          <Button variant="success" icon={Plus} onClick={() => setShowAddPayment(true)} className="w-full sm:w-auto">
            Add Payment
          </Button>
        </div>

        {paymentsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader fullScreen={false} size="default" message="Loading payments..." />
          </div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No payments recorded yet</div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[700px] border-collapse">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Payment ID</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Mode</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Reference</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payments.map((payment, index) => (
                    <tr key={payment.id} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">{payment.payment_id}</td>
                      <td className="px-5 py-3 text-right text-sm font-semibold text-green-600">{formatCurrency(payment.amount)}</td>
                      <td className="px-5 py-3 text-sm text-gray-900">{formatDate(payment.payment_date)}</td>
                      <td className="px-5 py-3 text-sm text-gray-900">{payment.payment_mode || 'N/A'}</td>
                      <td className="px-5 py-3 text-sm text-gray-900">{payment.reference_number || 'N/A'}</td>
                      <td className="px-5 py-3 text-center">
                        <RowActionsMenu
                          isOpen={openPaymentActionId === payment.id}
                          onToggle={() => setOpenPaymentActionId(openPaymentActionId === payment.id ? null : payment.id)}
                          onClose={() => setOpenPaymentActionId(null)}
                          menuRef={paymentActionRef}
                          onView={() => setViewPayment(payment.id)}
                          onEdit={() => setEditPayment(payment.id)}
                          onDelete={() => handleDeletePayment(payment.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3 p-4">
              {payments.map((payment) => (
                <div key={`payment-card-${payment.id}`} className="rounded-xl border border-gray-100 p-4 border-l-4 border-l-green-500">
                  <div className="flex justify-between items-start mb-3 gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 text-sm break-all">{payment.payment_id}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{formatDate(payment.payment_date)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold uppercase whitespace-nowrap">
                        {(payment.payment_mode || 'N/A').replace(/_/g, ' ')}
                      </span>
                      <RowActionsMenu
                        isOpen={openPaymentActionId === payment.id}
                        onToggle={() => setOpenPaymentActionId(openPaymentActionId === payment.id ? null : payment.id)}
                        onClose={() => setOpenPaymentActionId(null)}
                        menuRef={paymentActionRef}
                        onView={() => setViewPayment(payment.id)}
                        onEdit={() => setEditPayment(payment.id)}
                        onDelete={() => handleDeletePayment(payment.id)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Amount</p>
                      <p className="font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Reference</p>
                      <p className="font-semibold text-gray-800 break-all">{payment.reference_number || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showAddTrip && (
        <AddVehicleDailyTripModal
          contractId={contract.id}
          onSuccess={() => {
            fetchTrips()
            fetchContract()
          }}
          onClose={() => setShowAddTrip(false)}
        />
      )}

      {editTrip && (
        <EditVehicleDailyTripModal
          tripId={editTrip}
          onSuccess={() => {
            fetchTrips()
            fetchContract()
          }}
          onClose={() => setEditTrip(null)}
        />
      )}

      {viewTrip && (
        <ViewVehicleDailyTripModal
          tripId={viewTrip}
          onClose={() => setViewTrip(null)}
        />
      )}

      {showAddPayment && (
        <AddVehicleContractPaymentModal
          contractId={contract.id}
          onSuccess={() => {
            fetchPayments()
            fetchContract()
          }}
          onClose={() => setShowAddPayment(false)}
        />
      )}

      {editPayment && (
        <EditVehicleContractPaymentModal
          paymentId={editPayment}
          onSuccess={() => {
            fetchPayments()
            fetchContract()
          }}
          onClose={() => setEditPayment(null)}
        />
      )}

      {viewPayment && (
        <ViewVehicleContractPaymentModal
          paymentId={viewPayment}
          onClose={() => setViewPayment(null)}
        />
      )}
    </DashboardLayout>
  )
}

export default ViewVehicleContract
